import * as React from 'react';
import styled, { css } from 'styled-components';
import { colors } from './colors/color';
import { Icon, iconName } from './icons/Icons';
import { TextAvatar } from './avatar/TextAvatar';

export type TabberVariant = 'default' | 'slim' | 'text' | 'text-minimal';
export type TabberAlignment = 'left' | 'center' | 'right';

export type TabItem = {
    tabName:string,
    leadingContent?:iconName,
    avatarCount?:number,
    text:string,
    disabled?:boolean,
    hidden?:boolean
}

type TabberProps = {
    variant?:TabberVariant,
    inline?:boolean,
    alignment?:TabberAlignment,
    selectedTabName?:string,
    items:TabItem[],
    style?:React.CSSProperties,
    onClick?:(tabName:string)=>void
}
export function Tabber(props:TabberProps):JSX.Element
{
    function handleClickTab(item:TabItem)
    {
        props.onClick?.(item.tabName);
    }

    const styleVariant = props.variant || 'default';
    const styleAlignment = props.alignment || 'left';

    return (
        <TabContainer variant={styleVariant} alignment={styleAlignment} style={props.style}>
            {props.items.filter(t => !t.hidden).map(t => 
                <Tab key={t.tabName} 
                    variant={styleVariant}
                    selected={t.tabName === props.selectedTabName} 
                    disabled={!!t.disabled}
                    inline={!!props.inline}
                    onClick={() => handleClickTab(t)}>

                    {t.leadingContent && 
                            <Icon name={t.leadingContent} color={
                                t.disabled 
                                    ? colors.tabber.colorDisabled
                                    : t.tabName === props.selectedTabName ? colors.tabber.colorSelected : colors.tabber.color
                            } />
                    }
                    <TabText style={{marginRight:t.avatarCount ? '4px' : '0'}} disabled={!!t.disabled} selected={t.tabName === props.selectedTabName}>
                        {t.text}
                    </TabText>
                    {t.avatarCount !== undefined &&
                        <TextAvatar
                            backgroundColor={colors.avatar.backgroundLight}
                            text={t.avatarCount.toString()}
                            size='20px'
                        />
                    }
                </Tab>
            )}
        </TabContainer>
    );
}

const TabContainer = styled.div<{variant:TabberVariant, alignment:TabberAlignment}>`
    display: flex;
    justify-content:stretch;
    align-items: center;
    padding: 4px;
    width: 100%;
    border: 1px solid ${colors.tabber.border};

    ${props => props.variant === 'default' && css`
        gap: 4px;
    `}

    ${props => props.variant === 'slim' && css`
        gap: 4px;
    `}

    ${props => props.variant === 'text' && css`
        justify-content: ${props.alignment === 'center'
            ? 'center'
            : props.alignment === 'right'
                ? 'flex-end'
                : 'flex-start'};
        border:none;
    `}

    ${props => props.variant === 'text-minimal' && css`
        justify-content: ${props.alignment === 'center'
            ? 'center'
            : props.alignment === 'right'
                ? 'flex-end'
                : 'flex-start'};
        border:none;
    `}

`;
const Tab = styled.button<{selected:boolean, disabled:boolean, inline:boolean, variant:TabberVariant}>`
    border:none;
    display:flex;
    flex-grow: 1;
    gap: 4px;
    align-items:center;
    justify-content: center;
    min-height: 34px; // Must be set since icons are slightly larger than avatars and would increase height otherwise for only some buttons
    
    cursor: ${props => !props.disabled ? 'pointer' : 'default'};

    color:${props => props.disabled 
        ? colors.tabber.colorDisabled 
        : props.selected ? colors.tabber.colorSelected : colors.tabber.color
    };

    font-weight: ${props => props.selected ? 400 : 300};
    line-height: 22px;
    letter-spacing: 0.5px;

    ${props => props.variant === 'default' && css`
        background-color: ${props.selected ? colors.tabber.backgroundSelected : colors.background.white};
        border-radius: 4px;
        padding: 12px 24px;
        font-size: 16px;

        &:hover {
            border-radius: 4px;
            background-color: ${props.disabled ? "none" : colors.tabber.backgroundHover};
        }
    `}

    ${props => props.variant === 'slim' && css`
        background-color: ${props.selected ? colors.tabber.backgroundSelected : colors.background.white};
        border-radius: 4px;
        padding: 4px 20px;

        &:hover {
            border-radius: 4px;
            background-color: ${props.disabled ? "none" : colors.tabber.backgroundHover};
        }
    `}

    ${props => props.variant === 'text' && css`
        background-color: ${colors.background.white};
        padding: 4px 20px;
        border-bottom: 2px solid ${props.selected ? colors.tabber.colorSelected :  colors.tabber.border};
        &:hover, &:hover svg {
            ${!props.disabled && `color:${colors.tabber.colorSelected};` }
            ${!props.disabled && `fill:${colors.tabber.colorSelected};` }
            ${!props.disabled && `border-bottom-color:${colors.tabber.colorSelected2};`}
        }
    `}

    ${props => props.variant === 'text-minimal' && css`
        background-color: ${colors.background.white};
        padding: 4px 20px;
        
        color: ${props.selected
            ? colors.tabber.colorSelected
            : colors.tabber.colorDisabled
        };

        svg {
            color: ${props.selected
                ? colors.tabber.colorSelected
                : colors.tabber.colorDisabled
            };
            fill: ${props.selected
                ? colors.tabber.colorSelected
                : colors.tabber.colorDisabled
            };
        }

        &:hover, &:hover svg {
            ${!props.disabled && `color:${colors.tabber.colorSelected};` }
            ${!props.disabled && `fill:${colors.tabber.colorSelected};` }
            ${!props.disabled && `border-bottom-color:${colors.tabber.colorSelected2};`}
        }
    `}

    ${props => props.inline && css`
        flex-grow: 0;
    `}
`;

export const TabText = styled.span<{disabled:boolean, selected:boolean}>`
`;